import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import LabelToggle, {
  type LabelToggleOption,
} from 'ext-ui/components/LabelToggle/LabelToggle'
import React, { ReactNode, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  AccountBinInputPageCvcFlatFields,
  AccountBinInputPageFlatFields,
} from 'src/app/RegisterAccount/RegisterAccount'
import AppName from 'src/components/AppName/AppName'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import RegisterAccountBinInputForm from 'src/components/RegisterAccountBinInputForm/RegisterAccountBinInputForm'
import ConnectCardFeedView from 'src/components/RegisterAccountBinInputPage/ConnectCardFeedView'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import SvgLocked from 'src/components/Svgs/SvgLocked'
import { SupportedBrands } from 'src/enums'
import { useBackgroundDispatch, useFlyoutDispatch } from 'src/hooks'
import Trns, { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { closeModal } from 'src/navigation/Lnk'
import { cardFeedsUrl, registerAccountUrl } from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { getCreditCardsActiveSourceCountStatistics } from 'src/redux/actions/data/statistics.actions'
import { selectAllStatisticsLoadedFromState } from 'src/redux/selectors/app.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { useWhitelabel } from 'src/utils/whitelabel.utils'
import { css } from 'styled-components'

type EnrollmentTab = 'single' | 'feed'

const ENROLLMENT_TAB_VALUES: readonly EnrollmentTab[] = ['single', 'feed']
const isEnrollmentTab = (v: string): v is EnrollmentTab =>
  (ENROLLMENT_TAB_VALUES as readonly string[]).includes(v)

interface RegisterAccountBinInputPageProps {
  flatFields: AccountBinInputPageFlatFields
  onFlatFieldChange: (
    fieldName: keyof AccountBinInputPageFlatFields,
    value: AccountBinInputPageFlatFields[keyof AccountBinInputPageFlatFields],
  ) => void
  flatFieldsErrors: Partial<
    Record<
      keyof AccountBinInputPageFlatFields,
      Array<React.ReactElement | string>
    >
  >
  cvcFlatFields: AccountBinInputPageCvcFlatFields
  onCvcFlatFieldChange: (
    fieldName: keyof AccountBinInputPageCvcFlatFields,
    value: AccountBinInputPageCvcFlatFields[keyof AccountBinInputPageCvcFlatFields],
  ) => void
  cvcFlatFieldsErrors: Partial<
    Record<
      keyof AccountBinInputPageCvcFlatFields,
      Array<React.ReactElement | string>
    >
  >
  loading?: boolean
  onSubmit: (bin?: string) => void
  hasOrganization: boolean
  topLevelError?: string
}

const RegisterAccountBinInputPage: React.FC<
  RegisterAccountBinInputPageProps
> = ({
  flatFields,
  onFlatFieldChange,
  flatFieldsErrors,
  cvcFlatFields,
  onCvcFlatFieldChange,
  cvcFlatFieldsErrors,
  onSubmit,
  hasOrganization,
  topLevelError,
  loading,
}): React.ReactElement => {
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const translate = useTranslate()
  const { whiteLabelIncludes } = useWhitelabel()
  const { analytics } = useAnalyticsContext()

  const dispatch = useBackgroundDispatch()
  const flyoutDispatch = useFlyoutDispatch()
  const history = useHistory()

  const { data: userFeatures } = useQueryUserFeatures([
    UserFeature.BULK_CREDIT_CARD_ENROLLMENT,
  ])
  const bulkCreditCardEnrollmentEnabled =
    userFeatures?.BULK_CREDIT_CARD_ENROLLMENT ?? false
  const { enable_card_feeds: cardFeedsEnabled } =
    useSelector(selectFeatureFlaggerFromState) ?? {}
  const showEnrollmentToggle =
    bulkCreditCardEnrollmentEnabled &&
    !!cardFeedsEnabled &&
    !whiteLabelIncludes(SupportedBrands.Jpm)

  const [activeTab, setActiveTab] = useState<EnrollmentTab>('single')

  // Defensive reset: if the gate flips off (flag toggled mid-session, brand change),
  // snap back to the single-card view so the next render is consistent.
  useEffect(() => {
    if (!showEnrollmentToggle && activeTab !== 'single') {
      setActiveTab('single')
    }
  }, [showEnrollmentToggle, activeTab])

  let binInputTitle = translate('Add your company credit card')
  let binInputSubtitleText: string = showEnrollmentToggle
    ? translate('Choose how you want to bring your company cards into Extend.')
    : translate(
        'Enter your company credit card details in order to track expenses and control spending',
      )

  if (whiteLabelIncludes(SupportedBrands.Jpm)) {
    binInputTitle = hasOrganization
      ? translate('Register an Account')
      : translate('Add an account')

    binInputSubtitleText = hasOrganization
      ? translate(
          "Provide a payment method for your account. This credit card or bank account will be used to pay for expenses made with Virtual Cards. Let's start with your card information.",
        )
      : translate(
          "Provide a funding source for your account. This credit card or bank account will be used to pay for expenses made with Virtual Cards. Let's start with your card information.",
        )
  }

  // When the enrollment toggle is on, the subtitle is descriptive secondary text
  // and should render in the secondary gray to match Figma. Both the form and
  // the connect view wrap `subtitle` in a div coloured `font_primary`, so we
  // override locally with a styled span. Non-toggle flows (Extend default, JPM)
  // keep the original primary colour to avoid changing existing surfaces.
  const binInputSubtitle: ReactNode = showEnrollmentToggle ? (
    <span
      css={css`
        color: ${styleConstants.colors.gray_darkest};
      `}
    >
      {binInputSubtitleText}
    </span>
  ) : (
    binInputSubtitleText
  )

  useEffect((): void => {
    dispatch(getCreditCardsActiveSourceCountStatistics())
  }, [])

  useEffect(() => {
    analytics.track('add company card - viewed')
  }, [analytics])

  const toggleOptions: LabelToggleOption[] = [
    { value: 'single', displayName: translate('Enroll Single Card') },
    { value: 'feed', displayName: translate('Connect Card Feed') },
  ]

  const handleToggleChange = (option: LabelToggleOption): void => {
    if (isEnrollmentTab(option.value)) {
      setActiveTab(option.value)
      analytics.track('add company card', {
        action: 'tab selected',
        tab: option.value === 'feed' ? 'card_feed' : 'single_card',
      })
    }
  }

  const enrollmentToggle = showEnrollmentToggle ? (
    <div
      css={css`
        align-self: flex-start;
        margin-bottom: 30px;
      `}
    >
      <LabelToggle
        data-testid="card-enrollment-toggle"
        name="cardEnrollment"
        value={activeTab}
        onChange={handleToggleChange}
        options={toggleOptions}
      />
    </div>
  ) : null

  const handleCancelConnectCardFeed = (): void => {
    analytics.track('add company card', { action: 'cancel clicked' })
    flyoutDispatch(closeModal())
  }

  const handleGetStartedConnectCardFeed = (): void => {
    // Route-based modal: navigating off /accounts/create unmounts the Popup, so
    // history.push is sufficient. Dispatching closeModal() in addition would push
    // the backgroundLocation first and leave a spurious entry between cardfeeds
    // and the pre-modal page in the Back stack.
    history.push(cardFeedsUrl())
  }

  const cardInfoHeader = (
    <div
      css={css`
        margin-bottom: 20px;
      `}
    >
      <span
        id={'cardHeader'}
        css={css`
          ${getFontCss(
            20,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        <Trns source={'Card Information'} />
      </span>
      <div
        css={css`
          margin-top: 8px;
          display: flex;
          flex-direction: row;
        `}
      >
        <span
          css={css`
            display: flex;
            align-items: center;
            ${getFontCss(
              12,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          <SvgLocked
            css={css`
              transform-origin: left;
              transform: scale(calc(5 / 7));
            `}
          />
          <Trns
            source={
              'Security is our top priority. We meet or exceed security standards to handle your card information.'
            }
            expressions={{
              appName: <AppName />,
            }}
          />
        </span>
      </div>
    </div>
  )

  const showConnectCardFeedView = showEnrollmentToggle && activeTab === 'feed'

  return !statisticsLoaded.creditCardsActiveSourceCountStatisticsLoaded ? (
    <LoadingBackgroundPage />
  ) : (
    <>
      {showConnectCardFeedView ? (
        <ConnectCardFeedView
          title={binInputTitle}
          subtitle={binInputSubtitle}
          toggleSlot={enrollmentToggle}
          onCancel={handleCancelConnectCardFeed}
          onGetStarted={handleGetStartedConnectCardFeed}
        />
      ) : (
        <RegisterAccountBinInputForm
          flatFields={flatFields}
          onFlatFieldChange={onFlatFieldChange}
          flatFieldsErrors={flatFieldsErrors}
          cvcFlatFields={cvcFlatFields}
          onCvcFlatFieldChange={onCvcFlatFieldChange}
          cvcFlatFieldsErrors={cvcFlatFieldsErrors}
          onSubmit={onSubmit}
          title={binInputTitle}
          subtitle={binInputSubtitle}
          toggleSlot={enrollmentToggle}
          cardInfoHeader={cardInfoHeader}
          topLevelError={topLevelError}
          loading={loading}
        />
      )}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          background-color: ${styleConstants.colors.gray_light};
          padding: 40px 60px;
          border-radius: 0px 0px 10px 10px;
        `}
      >
        <p
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          {showConnectCardFeedView ? (
            <Trns
              source={
                'Setting up a card feed requires coordination with your issuing bank. ${learnMore}'
              }
              expressions={{
                learnMore: (
                  <ResourceCenterLink
                    underline
                    size={HoverableLinkSizes.Large}
                    analytics={{
                      event: 'add company card',
                      properties: {
                        action: 'learn what youll need clicked',
                      },
                    }}
                  >
                    <Trns source={"Learn what you'll need."} />
                  </ResourceCenterLink>
                ),
              }}
            />
          ) : (
            <Trns
              source={
                "Need help? Don't have a valid card number with your bank? Visit our ${resourceCenter} for support and contact options."
              }
              expressions={{
                resourceCenter: (
                  <ResourceCenterLink
                    underline
                    size={HoverableLinkSizes.Large}
                    analytics={{
                      event: 'register account - card info - contact support',
                      properties: {
                        intent: 'view "contact support"',
                        interaction: 'text link press',
                        screen: registerAccountUrl(),
                      },
                    }}
                  />
                ),
              }}
            />
          )}
        </p>
      </div>
    </>
  )
}

export default RegisterAccountBinInputPage
